import { Header } from 'antd/es/layout/layout';
import { useEffect, useLayoutEffect, useState } from 'react';

import {
  BarChartOutlined,
  CloseOutlined,
  MenuOutlined,
  SettingOutlined,
  SyncOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { Breadcrumb, Dropdown, Grid, MenuProps, Select, theme } from 'antd';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authActions } from '~/thunks/auth/authSlice';
import { language } from '~/utils/constants';
import logoutIcon from '../../assets/icons/logOut.svg';
import FlagEngland from '../../assets/imgs/flagEngland.svg';
import FlagItaly from '../../assets/imgs/flagItaly.svg';
import styles from './Header.module.scss';
import { icon } from '~/utils/constants.asset';
import { AdminProtectedPath, RouterPaths } from '~/utils/enum';
const cx = classNames.bind(styles);
const { Option } = Select;

interface IUser {
  email: string;
  language: string;
  role: string;
}

const HeaderLayout = () => {
  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const { i18n, t } = useTranslation();
  const { useBreakpoint } = Grid;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<IUser>();
  const [openSetting, setOpenSetting] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(language.IT);
  const dataLanguages = [
    { code: 'en', name: 'English', flag: FlagEngland },
    { code: 'it', name: 'Italian', flag: FlagItaly },
  ];
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const breadCrumbView = () => {
    const { pathname } = location;
    const pathnames = pathname
      .split('/')
      .filter((item) => item)
      .slice(1);
    const pathnamesObj = pathnames.map((item) => {
      return { title: t(`bread_crumb_${item}`) };
    });
    return <Breadcrumb items={pathnamesObj} />;
  };

  useLayoutEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
  }, []);

  useEffect(() => {
    i18n.changeLanguage(selectedLanguage);
  }, [selectedLanguage]);

  const settingItems: MenuProps['items'] = [
    {
      label: <div>{t('hello', { email: user?.email ? user?.email : 'Admin' })}</div>,
      key: '0',
    },
    {
      type: 'divider',
    },
    {
      label: (
        <div className={cx('header_logout')}>
          <img className={cx('header_logout-icon')} alt='logout' src={logoutIcon} /> {t('logout')}
        </div>
      ),
      key: 'logout',
    },
  ];

  const menuItems: MenuProps['items'] = [
    {
      label: (
        <div className={cx('header_menu_item')}>
          <SyncOutlined />
          {t('round_title')}
        </div>
      ),
      key: `/${RouterPaths.ADMIN}${AdminProtectedPath.ROUND.path}`,
    },
    {
      type: 'divider',
    },
    {
      label: (
        <div className={cx('header_menu_item')}>
          <TableOutlined />
          {t('table_title')}
        </div>
      ),
      key: `/${RouterPaths.ADMIN}${AdminProtectedPath.TABLE.path}`,
    },
    {
      type: 'divider',
    },
    {
      label: (
        <div className={cx('header_menu_item')}>
          <BarChartOutlined />
          {t('analytic_title')}
        </div>
      ),
      key: 'analytics',
      children: [
        {
          key: `/${RouterPaths.ADMIN}${AdminProtectedPath.ANALYTIC_TEAM.path}`,
          label: <div className={cx('header_submenu_item')}>{t('sidebar_team_title')}</div>,
        },
        {
          type: 'divider',
        },
        {
          key: `/${RouterPaths.ADMIN}${AdminProtectedPath.ANALYTIC_PLAYER.path}`,
          label: <div className={cx('header_submenu_item')}>{t('sidebar_player_title')}</div>,
        },
      ],
    },
  ];

  const handleLogout = () => {
    dispatch(authActions.handleLogout());
    navigate('/login');
  };
  const handleSettingClick: MenuProps['onClick'] = (e) => {
    if (e.key === 'logout') {
      setOpenSetting(false);
      handleLogout();
    }
  };
  const handleOpenSettingChange = (flag: boolean) => {
    setOpenSetting(flag);
  };
  const handleChangeLanguage = (language: string) => {
    setSelectedLanguage(language);
  };
  const handleOpenMenuChange = (flag: boolean) => {
    setOpenMenu(flag);
  };
  const handleMenuClick: MenuProps['onClick'] = (e) => {
    navigate(e.key);
  };

  return (
    <Header style={{ background: colorBgContainer }} className={cx('adminHeader')}>
      <div>
        {isMobile ? (
          <Link to={'/'}>
            <img src={icon.logoSideBar} alt={t('logo')} height={48} />
          </Link>
        ) : (
          breadCrumbView()
        )}
      </div>
      <div className={cx('header_action')}>
        <div className={cx('header_language')}>
          <Select
            onChange={(language) => handleChangeLanguage(language)}
            className={cx('language_select')}
            value={selectedLanguage}
            bordered={false}
            showArrow={false}
          >
            {dataLanguages.map((language) => (
              <Option key={language.code} value={language.code}>
                <div className={cx('language_item')}>
                  <img src={language.flag} className={cx('language_img')} alt='' /> {language.name}
                </div>
              </Option>
            ))}
          </Select>
        </div>
        <Dropdown
          menu={{
            items: settingItems,
            onClick: handleSettingClick,
          }}
          onOpenChange={handleOpenSettingChange}
          open={openSetting}
          trigger={['click']}
          overlayClassName={cx('dropdown-container')}
        >
          <a onClick={(e) => e.preventDefault()} className={cx('header_dropdown')}>
            <SettingOutlined className={cx('header_dropdown-icon')} />
          </a>
        </Dropdown>
        {isMobile && (
          <Dropdown
            menu={{
              items: menuItems,
              onClick: handleMenuClick,
              selectedKeys: [location.pathname],
            }}
            onOpenChange={handleOpenMenuChange}
            open={openMenu}
            trigger={['click']}
            overlayClassName={cx('dropdown-container')}
          >
            <a onClick={(e) => e.preventDefault()} className={cx('header_dropdown')}>
              {openMenu ? (
                <CloseOutlined className={cx('header_dropdown-icon')} />
              ) : (
                <MenuOutlined className={cx('header_dropdown-icon')} />
              )}
            </a>
          </Dropdown>
        )}
      </div>
    </Header>
  );
};

export default HeaderLayout;
